import React from 'react';
import moment from 'moment-timezone';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    Switch,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {grey} from "@mui/material/colors";
import {useTranslation} from "react-i18next";
import {FULL_DAY_DATE_FORMAT, MONTH_YEAR_FORMAT} from "../../config/DateTimeConstants";
import {SCHEDULE_PLANNER_VIEWS} from "../../constants/SchedulePlannerConstants";

const styles = {
    headerBox: {
        display: 'grid',
        gridTemplateColumns: {sm:'1fr auto', lg:'1fr 1fr 1fr'},
        gap: '1rem',
        marginBottom: '0.5rem',
        paddingX: '1rem',
        paddingTop: '0.25rem',
        alignItems: 'center',
    },
    leftControls: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        flexWrap: 'nowrap',
    },
    dateNavigator: {
        height: '2rem',
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'center',
    },
    datePicker: {
        '& .MuiInputBase-root': {
            height: '2rem'
        }
    },
    navButton: {
        borderRadius: '1rem',
        borderColor: grey[400],
        minWidth: '2rem',
        height: '2rem',
    },
    navIcon: {
        color: "black",
        fontSize: "0.95rem",
    },
    refreshButton: {
        padding: '0 0.4rem',
        width: '2rem',
        minWidth: '2rem',
        height: '2rem'
    },
    addShiftButton: {
        height: '2rem',
        minWidth: '6rem',
        flexWrap: 'nowrap',
        whiteSpace: 'nowrap',
    },
    formControl: {
        '& .MuiInputBase-root': {
            height: '2rem',
        },
        width: '8rem'
    },
    serviceMenuItem: {
        minHeight: '2rem',
        paddingX: '0.8rem',
        backgroundColor: "#ffffff",
        '&:hover': {
            backgroundColor: "#ffffff",
        },
        '&.Mui-selected': {
            backgroundColor: "#ffffff",
        },
        '&.Mui-selected:hover': {
            backgroundColor: "#ffffff",
        },
    },
    viewToggleGroup: {
        '& .MuiToggleButton-root': {
            minWidth: '4.5rem',
            height: '2rem',
            paddingInline: '1rem',
        },
        '& .MuiToggleButton-root.Mui-selected': {
            backgroundColor: 'primary.main',
            color: '#ffffff'
        },
        '& .MuiToggleButton-root.Mui-selected:hover': {
            backgroundColor: 'primary.main',
            color: '#ffffff'
        }
    }
};

const SchedulePlannerToolbar = ({
    onRefresh,
    currentDate,
    onDateChange,
    onGoPrevious,
    onGoNext,
    availableServiceTypes = [],
    activeView,
    onViewChange,
    setAddShiftPlanDialogOpen,
    selectedServiceTypeKeycodes = [],
    onServiceTypeChange
}) => {
    const {t} = useTranslation();

    return (
        <Box sx={styles.headerBox}>
            <Box sx={styles.leftControls}>
                <Button
                    className="icon-button-style"
                    variant='contained'
                    size='small'
                    sx={styles.refreshButton}
                    onClick={onRefresh}
                    endIcon={<RefreshIcon />} />
                <ToggleButtonGroup
                    exclusive
                    value={activeView}
                    onChange={onViewChange}
                    size="small"
                    aria-label="view toggle"
                    sx={styles.viewToggleGroup}>
                    <ToggleButton value={SCHEDULE_PLANNER_VIEWS.MONTH}>{t("shiftAdmin.toolbar.monthView")}</ToggleButton>
                    <ToggleButton value={SCHEDULE_PLANNER_VIEWS.DAY}>{t("shiftAdmin.toolbar.dayView")}</ToggleButton>
                </ToggleButtonGroup>
                {
                    activeView === SCHEDULE_PLANNER_VIEWS.DAY
                    &&
                    <>
                        <Button
                            size="small"
                            variant="contained"
                            sx={styles.addShiftButton}
                            onClick={()=>setAddShiftPlanDialogOpen(true)}>
                            {t("schedulePlanner.toolbar.addShiftPlan")}
                        </Button>
                        <FormControl sx={styles.formControl} size="small">
                            <InputLabel id="service-filter-label" shrink>
                                {t("shiftAdmin.toolbar.servicesFilterLabel")}
                            </InputLabel>
                            <Select
                                multiple
                                displayEmpty
                                labelId="service-filter-label"
                                value={selectedServiceTypeKeycodes}
                                label={t("shiftAdmin.toolbar.servicesFilterLabel")}
                                onChange={onServiceTypeChange}
                                renderValue={(selectedServiceTypes) => (`${selectedServiceTypes.length} Selected`)}
                                MenuProps={{MenuListProps: {sx: {paddingBottom: 0}}}}>
                                {availableServiceTypes.map((serviceType) => (
                                    <MenuItem key={serviceType.keycode} value={serviceType.keycode} sx={styles.serviceMenuItem}>
                                        <ListItemText primary={serviceType.keycode}/>
                                        <Switch size="small" checked={selectedServiceTypeKeycodes.includes(serviceType.keycode)} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </>
                }
            </Box>

            <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={styles.dateNavigator}>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={onGoPrevious}
                    aria-label="previous date"
                    sx={styles.navButton}>
                    <ArrowBackIosNewIcon sx={styles.navIcon}/>
                </Button>

                <DatePicker
                    value={moment(currentDate)}
                    onChange={onDateChange}
                    views={activeView === SCHEDULE_PLANNER_VIEWS.MONTH ? ["year", "month"] : ["year", "month", "day"]}
                    format={activeView === SCHEDULE_PLANNER_VIEWS.MONTH ? MONTH_YEAR_FORMAT : FULL_DAY_DATE_FORMAT}
                    slotProps={{
                        textField: {
                            size: "small",
                        }
                    }}
                    sx={styles.datePicker}/>

                <Button
                    size="small"
                    variant="outlined"
                    onClick={onGoNext}
                    aria-label="next date"
                    sx={styles.navButton}>
                    <ArrowForwardIosIcon sx={styles.navIcon}/>
                </Button>
            </Stack>
        </Box>
    );
};

export default SchedulePlannerToolbar;